/**
 * Canonical drawer/sidebar IA (2026 menu consolidation).
 * ESM source for Vite/Rollup/Metro. The CJS sibling (`navConfig.cjs`) is auto-generated —
 * edit HERE and run `npm run sync:jtest-cjs` to regenerate.
 */

import { filterDeprioritizedRecoveryLinks } from './betaFocusMode.js';
import { filterNavItemsByDoors } from './productDoors.js';

export const NAV_TRAIN_ITEMS = [
  {
    name: 'Analytics',
    screen: 'Analytics',
    href: '/analytics',
    webIconKey: 'BarChart3',
    pro: true,
    drawerOnly: true,
  },
  {
    name: 'Recovery',
    screen: 'Recovery',
    href: '/recovery',
    webIconKey: 'HeartPulse',
    drawerOnly: true,
  },
  { name: 'Calendar', screen: 'Calendar', href: '/calendar', webIconKey: 'Calendar' },
  { name: 'Activities', screen: 'Activities', href: '/activities', webIconKey: 'Activity', tabRootMobile: true },
  { name: 'Routes', screen: 'Routes', href: '/routes', webIconKey: 'Map' },
  {
    name: 'Records',
    screen: 'PersonalBests',
    href: '/personal-bests',
    webIconKey: 'Trophy',
  },
  {
    name: 'Trainer Mode',
    screen: 'TrainerSession',
    href: '/trainer',
    webIconKey: 'Bike',
    pro: true,
  },
  {
    name: 'Coach',
    screen: 'Coach',
    href: '/coach',
    webIconKey: 'MessageCircle',
    pro: true,
    coachNav: true,
  },
];

export const NAV_TRAIN_WEB_EXTRA = [
  { name: 'Dashboard', screen: 'Dashboard', href: '/dashboard', webIconKey: 'Home', webOnly: true },
  {
    name: 'Training Plans',
    screen: 'Plans',
    href: '/training-plans',
    webIconKey: 'Target',
    webOnly: true,
    tabRootMobile: true,
  },
  {
    name: 'Analytics',
    screen: 'Analytics',
    href: '/analytics',
    webIconKey: 'BarChart3',
    pro: true,
    webOnly: true,
  },
];

export const NAV_BONK_ITEMS = [
  {
    name: 'Bonk Risk Index',
    screen: 'BonkHistory',
    href: '/bonk-history',
    webIconKey: 'BarChart3',
    pro: true,
  },
  {
    name: 'Bonk Lab',
    screen: 'BonkLab',
    href: '/bonk-lab',
    webIconKey: 'Zap',
    pro: true,
  },
];

/** Fuel utilities in More tools (mobile home dashboard footer + web Nutrition header), not drawer rows. */
export const NAV_NUTRITION_HUB_LINKS = [
  {
    name: 'Quick-fill day',
    screen: 'QuickFill',
    href: '/nutrition/quick-fill',
    webIconKey: 'ClipboardList',
  },
  {
    name: 'Recovery drink advisor',
    screen: 'RecoveryDrink',
    href: '/recovery',
    webIconKey: 'Beaker',
  },
  {
    name: 'Fueling history',
    screen: 'FuelingHistory',
    href: '/fueling-history',
    webIconKey: 'BarChart3',
  },
];

/** Linked from Recovery hub (mobile Recovery tab + web Recovery page), not drawer rows. */
export const NAV_RECOVERY_HUB_LINKS = [
  {
    name: 'Post-ride drink advisor',
    screen: 'RecoveryDrink',
    href: '/recovery',
    webIconKey: 'Beaker',
    blurb: 'Post-ride fluids and carbs matched to your session.',
  },
  {
    name: 'Go to Mobility',
    screen: 'Mobility',
    href: '/mobility',
    webIconKey: 'HeartPulse',
    blurb: 'Warm-up and cool-down routines for recovery days.',
  },
];

/** Canonical consumer for hub UI; applies beta-focus + product-door filtering. */
export function getRecoveryHubLinks(opts = {}) {
  const betaFiltered = filterDeprioritizedRecoveryLinks(NAV_RECOVERY_HUB_LINKS, opts);
  return filterNavItemsByDoors(betaFiltered, opts.doors);
}

export const NAV_FUEL_GROUPS = [];

/** Deprioritized surfaces: routes stay registered; hidden from primary nav and Calendar mobility panel. */
export const SHOW_MOBILITY_ON_CALENDAR = false;

export const NAV_FUEL_WEB_TAB_ITEMS = [
  { name: 'Nutrition', screen: 'Nutrition', href: '/nutrition', webIconKey: 'UtensilsCrossed', webOnly: true, tabRootMobile: true },
  { name: 'Recovery', screen: 'Recovery', href: '/recovery', webIconKey: 'HeartPulse', webOnly: true },
];

/** Race pacing lives on Plans tab + Race Day hub; no drawer/sidebar section. */
export const NAV_RACE_ITEMS = [];

export const NAV_ACCOUNT_ITEMS = [
  { name: 'Profile', screen: 'Profile', href: '/profile', webIconKey: 'User' },
  { name: 'Settings', screen: 'Settings', href: '/settings', webIconKey: 'Settings' },
];

/**
 * Deprioritized Labs surfaces (stack-only by default).
 * When `MOBILE_LABS_ENABLED` is false in `apps/mobile/lib/mobile-nav-config.ts`,
 * these stay in NAV_HIDDEN_SCREENS and are not promoted in the drawer.
 */
export const NAV_LABS_SCREENS = Object.freeze([
  'DIYNutrition',
  'LiveRide',
  'Mobility',
  'BikeFit',
  'RewardsDeals',
  'Trophies',
]);

/** Drawer rows for Labs surfaces; shown only when MOBILE_LABS_ENABLED is true. */
export const NAV_LABS_DRAWER_ITEMS = [
  { name: 'DIY Fuel Lab', screen: 'DIYNutrition', labsBadge: true },
  { name: 'Mobility', screen: 'Mobility', labsBadge: true },
  { name: 'Bike Fit', screen: 'BikeFit', labsBadge: true },
];

/** Stack screens reachable via in-app navigation but not listed in drawer/sidebar rows. */
export const NAV_HIDDEN_SCREENS = [
  'MealPlanHistory',
  ...NAV_LABS_SCREENS,
  'RaceDayReady',
  'RaceDayQuick',
  'LogActivity',
  'WeightNutritionInsights',
  'QuickFill',
  'RecoveryDrink',
  'FuelingHistory',
  'BonkLab',
  'Coach',
];

export function navItemsForWebSidebar(items) {
  return items.filter((item) => !item.drawerOnly);
}

export function navItemsForMobileDrawer(items) {
  return items.filter((item) => !item.webOnly && !item.tabRootMobile);
}

export function flattenFuelNavItems() {
  return NAV_FUEL_GROUPS.flatMap((g) => [...g.items]);
}

export function buildWebNavSectionLinks() {
  const trainItems = [
    ...NAV_TRAIN_WEB_EXTRA,
    ...navItemsForWebSidebar(NAV_TRAIN_ITEMS),
    ...navItemsForWebSidebar(NAV_BONK_ITEMS),
  ].map((item) => ({
    name: item.name,
    href: item.href,
    iconKey: item.webIconKey ?? 'Activity',
    ...(item.pro ? { proFeature: true } : {}),
  }));

  const fuelFlat = [
    ...NAV_FUEL_WEB_TAB_ITEMS,
    ...navItemsForWebSidebar(flattenFuelNavItems()),
  ].map((item) => ({
    name: item.name,
    href: item.href,
    iconKey: item.webIconKey ?? 'ClipboardList',
    ...(item.pro ? { proFeature: true } : {}),
  }));

  const sections = [
    { id: 'main', label: '', items: [...trainItems, ...fuelFlat] },
  ];

  const raceItems = navItemsForWebSidebar(NAV_RACE_ITEMS).map((item) => ({
    name: item.name,
    href: item.href,
    iconKey: item.webIconKey ?? 'Flag',
    ...(item.pro ? { proFeature: true } : {}),
  }));
  if (raceItems.length > 0) {
    sections.push({ id: 'race', label: 'Race', items: raceItems });
  }

  return sections;
}

export function buildMobileTrainNavItems() {
  const trainRows = navItemsForMobileDrawer(NAV_TRAIN_ITEMS).map(({ name, screen, pro }) => ({
    name,
    screen,
    ...(pro ? { pro } : {}),
  }));
  const bonkRows = navItemsForMobileDrawer(NAV_BONK_ITEMS).map(({ name, screen, pro }) => ({
    name,
    screen,
    ...(pro ? { pro } : {}),
  }));
  return [...trainRows, ...bonkRows];
}

export function buildMobileFuelNavGroups() {
  return NAV_FUEL_GROUPS.map((group) => ({
    id: group.id,
    items: navItemsForMobileDrawer(group.items).map(({ name, screen, pro, labsBadge }) => ({
      name,
      screen,
      ...(pro ? { pro } : {}),
      ...(labsBadge ? { labsBadge: true } : {}),
    })),
  }));
}

export function buildMobileBonkNavItems() {
  return navItemsForMobileDrawer(NAV_BONK_ITEMS).map(({ name, screen, pro }) => ({
    name,
    screen,
    ...(pro ? { pro } : {}),
  }));
}

export function buildMobileRaceNavItems() {
  return navItemsForMobileDrawer(NAV_RACE_ITEMS).map(({ name, screen, pro }) => ({
    name,
    screen,
    ...(pro ? { pro } : {}),
  }));
}

export function buildMobileLabsNavItems() {
  return NAV_LABS_DRAWER_ITEMS.map(({ name, screen, labsBadge }) => ({
    name,
    screen,
    ...(labsBadge ? { labsBadge: true } : {}),
  }));
}

export function drawerI18nScreenNames() {
  const sections = [
    buildMobileTrainNavItems(),
    flattenFuelNavItems().filter((i) => !i.webOnly && !i.tabRootMobile),
    buildMobileRaceNavItems(),
    NAV_ACCOUNT_ITEMS.map(({ name, screen }) => ({ name, screen })),
  ];
  const seen = new Set();
  const screens = [];
  for (const section of sections) {
    for (const item of section) {
      if (seen.has(item.screen)) continue;
      seen.add(item.screen);
      screens.push(item.screen);
    }
  }
  return screens;
}
